import { Plus, X } from 'lucide-react';
import CollapsibleSection from './CollapsibleSection';
import ListInput from './ListInput';

const fieldClass = "w-full px-2.5 py-1.5 text-sm border border-stone-300 rounded bg-white focus:outline-none focus:border-stone-600 focus:ring-1 focus:ring-stone-600 transition-colors";
const labelClass = "block text-[11px] uppercase tracking-wider text-stone-500 mb-1 font-medium";
const subSectionClass = "border border-stone-200 rounded-md p-3 bg-stone-50/50";

export default function ResumeForm({ data, setData }) {
  const update = (path, value) => {
    setData(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const updateExp = (i, key, val) => {
    setData(prev => ({
      ...prev,
      experience: prev.experience.map((e, idx) => idx === i ? { ...e, [key]: val } : e),
    }));
  };

  const updateEdu = (i, key, val) => {
    setData(prev => ({
      ...prev,
      education: prev.education.map((e, idx) => idx === i ? { ...e, [key]: val } : e),
    }));
  };

  const updateCert = (i, key, val) => {
    setData(prev => ({
      ...prev,
      certifications: prev.certifications.map((e, idx) => idx === i ? { ...e, [key]: val } : e),
    }));
  };

  return (
    <div className="space-y-1">
      <CollapsibleSection title="Identity & Contact">
        <div>
          <label className={labelClass}>Full name</label>
          <input className={fieldClass} value={data.name} onChange={e => update('name', e.target.value)} placeholder="Your name" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelClass}>Phone</label>
            <input className={fieldClass} value={data.contact.phone} onChange={e => update('contact.phone', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input className={fieldClass} value={data.contact.email} onChange={e => update('contact.email', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Location</label>
            <input className={fieldClass} value={data.contact.location} onChange={e => update('contact.location', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>LinkedIn</label>
            <input className={fieldClass} value={data.contact.linkedin} onChange={e => update('contact.linkedin', e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className={labelClass}>Website</label>
            <input className={fieldClass} value={data.contact.website} onChange={e => update('contact.website', e.target.value)} />
          </div>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Summary">
        <div>
          <label className={labelClass}>Tagline (italic)</label>
          <input className={fieldClass} value={data.summary.tagline} onChange={e => update('summary.tagline', e.target.value)} placeholder="e.g. Multi-faceted retail executive with expertise in:" />
        </div>
        <ListInput label="Bullets" items={data.summary.bullets} onChange={v => update('summary.bullets', v)} placeholder="A short summary point" />
        <ListInput label="Skills" items={data.summary.skills} onChange={v => update('summary.skills', v)} placeholder="A skill" />
      </CollapsibleSection>

      <CollapsibleSection title="Experience">
        {data.experience.map((exp, i) => (
          <div key={i} className={subSectionClass}>
            <div className="flex justify-between items-start mb-2">
              <span className="text-xs uppercase tracking-wider text-stone-500 font-medium">Role {i + 1}</span>
              <button onClick={() => setData(p => ({ ...p, experience: p.experience.filter((_, j) => j !== i) }))}
                className="text-stone-400 hover:text-red-600 transition-colors" aria-label="Remove role">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input className={fieldClass} placeholder="Title" value={exp.title} onChange={e => updateExp(i, 'title', e.target.value)} />
                <input className={fieldClass} placeholder="Company" value={exp.company} onChange={e => updateExp(i, 'company', e.target.value)} />
                <input className={fieldClass} placeholder="Location" value={exp.location} onChange={e => updateExp(i, 'location', e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <input className={fieldClass} placeholder="Start" value={exp.start} onChange={e => updateExp(i, 'start', e.target.value)} />
                  <input className={fieldClass} placeholder="End" value={exp.end} onChange={e => updateExp(i, 'end', e.target.value)} />
                </div>
              </div>
              <textarea className={fieldClass} rows={2} placeholder="Optional summary paragraph" value={exp.summary} onChange={e => updateExp(i, 'summary', e.target.value)} />
              <ListInput label="Bullets" items={exp.bullets} onChange={v => updateExp(i, 'bullets', v)} placeholder="A bullet point" />
            </div>
          </div>
        ))}
        <button
          onClick={() => setData(p => ({ ...p, experience: [...p.experience, { title: "", company: "", location: "", start: "", end: "", summary: "", bullets: [] }] }))}
          className="w-full py-2 border border-dashed border-stone-300 text-stone-600 hover:bg-stone-50 hover:border-stone-400 rounded text-sm flex items-center justify-center gap-1 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add role
        </button>
      </CollapsibleSection>

      <CollapsibleSection title="Education">
        {data.education.map((ed, i) => (
          <div key={i} className={subSectionClass}>
            <div className="flex justify-between items-start mb-2">
              <span className="text-xs uppercase tracking-wider text-stone-500 font-medium">Entry {i + 1}</span>
              <button onClick={() => setData(p => ({ ...p, education: p.education.filter((_, j) => j !== i) }))}
                className="text-stone-400 hover:text-red-600" aria-label="Remove education">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input className={fieldClass} placeholder="Degree" value={ed.degree} onChange={e => updateEdu(i, 'degree', e.target.value)} />
              <input className={fieldClass} placeholder="School" value={ed.school} onChange={e => updateEdu(i, 'school', e.target.value)} />
              <input className={fieldClass} placeholder="Location" value={ed.location} onChange={e => updateEdu(i, 'location', e.target.value)} />
              <input className={fieldClass} placeholder="Date" value={ed.date} onChange={e => updateEdu(i, 'date', e.target.value)} />
              <input className={fieldClass + " col-span-2"} placeholder="Honors (optional)" value={ed.honors} onChange={e => updateEdu(i, 'honors', e.target.value)} />
            </div>
          </div>
        ))}
        <button
          onClick={() => setData(p => ({ ...p, education: [...p.education, { degree: "", school: "", location: "", date: "", honors: "" }] }))}
          className="w-full py-2 border border-dashed border-stone-300 text-stone-600 hover:bg-stone-50 hover:border-stone-400 rounded text-sm flex items-center justify-center gap-1 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add education
        </button>
      </CollapsibleSection>

      <CollapsibleSection title="Certifications" defaultOpen={false}>
        {data.certifications.map((c, i) => (
          <div key={i} className={subSectionClass}>
            <div className="flex justify-between items-start mb-2">
              <span className="text-xs uppercase tracking-wider text-stone-500 font-medium">Certification {i + 1}</span>
              <button onClick={() => setData(p => ({ ...p, certifications: p.certifications.filter((_, j) => j !== i) }))}
                className="text-stone-400 hover:text-red-600" aria-label="Remove certification">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input className={fieldClass + " col-span-2"} placeholder="Name" value={c.name} onChange={e => updateCert(i, 'name', e.target.value)} />
              <input className={fieldClass} placeholder="Date" value={c.date} onChange={e => updateCert(i, 'date', e.target.value)} />
              <input className={fieldClass + " col-span-3"} placeholder="Issuing organization" value={c.org} onChange={e => updateCert(i, 'org', e.target.value)} />
            </div>
          </div>
        ))}
        <button
          onClick={() => setData(p => ({ ...p, certifications: [...p.certifications, { name: "", org: "", date: "" }] }))}
          className="w-full py-2 border border-dashed border-stone-300 text-stone-600 hover:bg-stone-50 hover:border-stone-400 rounded text-sm flex items-center justify-center gap-1 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add certification
        </button>
      </CollapsibleSection>

      <CollapsibleSection title="Awards" defaultOpen={false}>
        <ListInput label="Awards & Honors" items={data.awards} onChange={v => update('awards', v)} placeholder="An award or honor" />
      </CollapsibleSection>
    </div>
  );
}
